import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$49',
    period: '/month',
    description: 'Perfect for small strata councils managing 1–2 buildings.',
    features: [
      'Up to 2 buildings',
      'Work order management',
      'Document vault (5 GB)',
      'Email support',
    ],
    highlight: false,
    badge: null,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$99',
    period: '/month',
    description: 'For growing property managers with multiple buildings.',
    features: [
      'Up to 10 buildings',
      'AI Meeting Assistant',
      'Smart Document Insights',
      'Predictive Maintenance',
      'Owner Portal',
      'Priority support',
    ],
    highlight: true,
    badge: 'Most Popular',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$199',
    period: '/month',
    description: 'Unlimited scale for large strata management companies.',
    features: [
      'Unlimited buildings',
      'All Professional features',
      'Custom integrations',
      'Dedicated account manager',
      'SLA support',
    ],
    highlight: false,
    badge: null,
  },
];

export default function Subscribe() {
  const [loading, setLoading] = useState(null); // plan id being processed
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription activated! Welcome to StrataPulse.');
      localStorage.removeItem('blocked_message');
      setTimeout(() => navigate('/'), 1500);
    }
    if (searchParams.get('cancelled') === 'true') {
      toast('Checkout cancelled — you can try again any time.', { icon: 'ℹ️' });
    }
  }, [searchParams, navigate]);

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const res = await api.post('/api/create-checkout-session', { plan: planId });
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not start checkout. Please try again.';
      toast.error(msg);
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 py-16 px-4">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-600 rounded-2xl mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">Choose Your Plan</h1>
          <p className="text-slate-300 text-lg max-w-xl mx-auto">
            Simple, transparent pricing. Cancel any time. All plans include a 30-day money-back guarantee.
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 flex flex-col ${
                plan.highlight
                  ? 'bg-blue-600 text-white shadow-2xl shadow-blue-500/30 ring-2 ring-blue-400'
                  : 'bg-white/10 text-white border border-white/20'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1 rounded-full shadow">
                  {plan.badge}
                </span>
              )}

              <div className="mb-4">
                <h2 className="text-xl font-bold mb-1">{plan.name}</h2>
                <p className={`text-sm ${plan.highlight ? 'text-blue-100' : 'text-slate-300'}`}>
                  {plan.description}
                </p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-extrabold">{plan.price}</span>
                <span className={`text-sm ${plan.highlight ? 'text-blue-100' : 'text-slate-400'}`}>
                  {plan.period}
                </span>
              </div>

              <ul className="space-y-2.5 flex-1 mb-6">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <svg className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? 'text-blue-200' : 'text-green-400'}`}
                      fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={!!loading}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  plan.highlight
                    ? 'bg-white text-blue-600 hover:bg-blue-50'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
              >
                {loading === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Redirecting…
                  </span>
                ) : `Get ${plan.name}`}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-slate-400 text-sm">
          Questions?{' '}
          <a href="mailto:hello@getstratapulse.com" className="text-blue-400 hover:text-blue-300 underline">
            hello@getstratapulse.com
          </a>
        </p>
      </div>
    </div>
  );
}
